import {useState} from 'react';
import cx from 'classnames';
import NextLink from 'next/link';

import {AnimatedText, Button, Icon, NumberTicker} from '#component/base';
import {ModalRoot, ModalTrigger} from '#component/base/Modal';
import {LifestyleScoreInfoModal} from '#component/modal';
import {KitRegisteredPill, LifestyleMeter} from '#component/partial';
import {Transition} from '#component/util';
import {useOnceEffect} from '#hook';
import {headerTransition} from '#style/keyframes.css';
import {sprinkles} from '#style/sprinkles';
import {getLifestyleScoreTitle} from '#util/lifestyleScore';

import {LifestyleHeaderProps} from './LifestyleHeader.types';

import * as css from './LifestyleHeader.css';

export const LifestyleHeader = ({
  className,
  maxScore = 100,
  score = 0,
  registeredDate,
  showLifestylePreview = false,
}: LifestyleHeaderProps) => {
  const [animate, setAnimate] = useState(false);

  useOnceEffect(() => {
    setTimeout(() => {
      setAnimate(true);
    }, 0);
  });

  return (
    <Transition appear show className={cx(css.root, className)}>
      <div className={css.contentContainer}>
        <div className={css.titleContainer}>
          <Transition.Child className={css.scoreContainerStyles}>
            <NumberTicker
              appear
              className={css.scoreStyle}
              color="deepBurgundy"
              fontWeight="ultraLight">
              {score}
            </NumberTicker>
            <span className={css.maxScoreStyle}>{`/ ${maxScore}`}</span>
          </Transition.Child>

          <div className={css.title}>
            <ModalRoot>
              <h1>
                <AnimatedText as="span" color="deepBurgundy" variant="heading1">
                  {`Your current lifestyle is ${getLifestyleScoreTitle(score)}`}
                </AnimatedText>

                <ModalTrigger>
                  <Transition.Child>
                    <Icon
                      aria-label="Learn more about Lifestyle Scores"
                      color="eggplantDark"
                      name="Info"
                      size="normal"
                    />
                  </Transition.Child>
                </ModalTrigger>
              </h1>

              <LifestyleScoreInfoModal />
            </ModalRoot>

            {!!registeredDate && <KitRegisteredPill registeredDate={registeredDate} />}
          </div>
        </div>

        <div className={sprinkles({flex: 'flex', dir: 'col', gap: 40})}>
          <LifestyleMeter maxValue={maxScore} value={animate ? score : 0} withGlow withHighlight />

          {showLifestylePreview && (
            <Transition.Child className={headerTransition}>
              <NextLink href="/lifestylepreview" passHref>
                <Button
                  as="a"
                  className={cx(
                    css.lifestylePreviewButtonStyles,
                    sprinkles({
                      pos: 'relative',
                      w: ['full', 'full', 'maxContent'],
                      z: 1,
                    }),
                  )}
                  variant="secondary">
                  Preview your new summary
                </Button>
              </NextLink>
            </Transition.Child>
          )}
        </div>
      </div>
    </Transition>
  );
};
